/**
 * HR service for handling HR document management and chat operations
 */

import { api } from './api'
import { HRMessage, HRChatSession } from '@/types/chat.types'

// HR Document types
export interface HRDocument {
  id: string
  title: string
  description: string
  type: string
  size: string
  lastUpdated: string
  category: string
  filename: string
  uploadedBy: string
  status: 'PROCESSING' | 'INDEXED' | 'FAILED' | 'UPLOADED' | 'PROCESSING_FAILED'
  url?: string
}

export interface DocumentStats {
  totalDocuments: number
  documentsByCategory: Record<string, number>
  recentUploads: number
  totalSize: string
}

export interface UploadResponse {
  message: string
  document: HRDocument
}

export interface DocumentListResponse {
  documents: HRDocument[]
  pagination?: {
    totalItems: number
    currentPage: number
    itemsPerPage: number
    totalPages: number
  }
}

export interface DocumentResponse {
  document: HRDocument
}

export interface DocumentUrlResponse {
  url: string
  expiresAt: string
}

// Upload parameters
export interface UploadDocumentParams {
  file: File
  title?: string
  description?: string
  category?: string
}

// Query parameters for filtering documents
export interface DocumentListParams {
  category?: string
  search?: string
  status?: string
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

/**
 * HR service class for document management operations
 */
export class HRService {
  private static readonly HR_ENDPOINTS = {
    UPLOAD_DOCUMENT: '/hr/documents/upload',
    DOCUMENTS: '/hr/documents',
    MY_DOCUMENTS: '/hr/documents/my',
    DOCUMENT_STATS: '/hr/documents/stats',
    DOCUMENT_BY_ID: (id: string) => `/hr/documents/${id}`,
    DOWNLOAD_DOCUMENT: (id: string) => `/hr/documents/${id}/download`,
    DOCUMENT_URL: (id: string) => `/hr/documents/${id}/url`,
    DOCUMENT_STATUS: (id: string) => `/hr/documents/${id}/status`,
  } as const

  /**
   * Upload a new document
   */
  static async uploadDocument(params: UploadDocumentParams): Promise<UploadResponse> {
    const formData = new FormData()
    formData.append('document', params.file)
    
    if (params.title) formData.append('title', params.title)
    if (params.description) formData.append('description', params.description)
    if (params.category) formData.append('category', params.category)

    const response = await api.post<UploadResponse>(
      this.HR_ENDPOINTS.UPLOAD_DOCUMENT,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    return response
  }

  /**
   * Get all documents with optional filtering and pagination
   */
  static async getDocuments(params: DocumentListParams = {}): Promise<DocumentListResponse> {
    const queryParams = new URLSearchParams()
    
    if (params.category) queryParams.append('category', params.category)
    if (params.search) queryParams.append('search', params.search)
    if (params.status) queryParams.append('status', params.status)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)

    const url = `${this.HR_ENDPOINTS.DOCUMENTS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get<DocumentListResponse>(url)

    return response
  }

  /**
   * Get current user's documents
   */
  static async getMyDocuments(params: DocumentListParams = {}): Promise<DocumentListResponse> {
    const queryParams = new URLSearchParams()
    
    if (params.category) queryParams.append('category', params.category)
    if (params.search) queryParams.append('search', params.search)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())

    const url = `${this.HR_ENDPOINTS.MY_DOCUMENTS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get<DocumentListResponse>(url)

    return response
  }

  /**
   * Get document statistics
   */
  static async getDocumentStats(): Promise<DocumentStats> {
    const response = await api.get<DocumentStats>(this.HR_ENDPOINTS.DOCUMENT_STATS)
    return response
  }

  /**
   * Get a specific document by ID
   */
  static async getDocument(id: string): Promise<DocumentResponse> {
    const response = await api.get<DocumentResponse>(
      this.HR_ENDPOINTS.DOCUMENT_BY_ID(id)
    )
    return response
  }

  /**
   * Download a document (returns blob for file download)
   */
  static async downloadDocument(id: string): Promise<Blob> {
    const response = await api.get<Blob>(
      this.HR_ENDPOINTS.DOWNLOAD_DOCUMENT(id),
      {
        responseType: 'blob',
      }
    )
    return response
  }

  /**
   * Get a secure URL for document access
   */
  static async getDocumentUrl(id: string): Promise<DocumentUrlResponse> {
    const response = await api.get<DocumentUrlResponse>(
      this.HR_ENDPOINTS.DOCUMENT_URL(id)
    )
    return response
  }

  /**
   * Update document metadata
   */
  static async updateDocument(
    id: string, 
    updates: Partial<Pick<HRDocument, 'title' | 'description' | 'category'>>
  ): Promise<{ document: HRDocument; message: string }> {
    const response = await api.put<{ document: HRDocument; message: string }>(
      this.HR_ENDPOINTS.DOCUMENT_BY_ID(id),
      updates
    )
    return response
  }

  /**
   * Delete a document
   */
  static async deleteDocument(id: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(
      this.HR_ENDPOINTS.DOCUMENT_BY_ID(id)
    )
    return response
  }

  /**
   * Get document status
   */
  static async getDocumentStatus(id: string): Promise<{ status: string }> {
    const response = await api.get<{ status: string }>(
      this.HR_ENDPOINTS.DOCUMENT_STATUS(id)
    )
    return response
  }

  /**
   * Update document status
   */
  static async updateDocumentStatus(
    id: string, 
    status: HRDocument['status']
  ): Promise<{ message: string }> {
    const response = await api.patch<{ message: string }>(
      this.HR_ENDPOINTS.DOCUMENT_STATUS(id),
      { status }
    )
    return response
  }

  /**
   * Helper method to trigger file download
   */
  static async triggerDownload(id: string, filename: string): Promise<void> {
    try {
      const blob = await this.downloadDocument(id)
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      
      // Trigger download
      document.body.appendChild(link)
      link.click()
      
      // Cleanup
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      throw error
    }
  }

  /**
   * HR Chat API methods
   */
  static readonly CHAT_ENDPOINTS = {
    SESSIONS: '/hr/chat/sessions',
    SESSION_BY_ID: (id: string) => `/hr/chat/sessions/${id}`,
    MESSAGES: '/hr/chat/messages',
    MESSAGE_FEEDBACK: (id: string) => `/hr/chat/messages/${id}/feedback`,
  } as const

  /**
   * Get chat sessions
   */
  static async getChatSessions(limit: number = 10): Promise<HRChatSession[]> {
    const response = await api.get<HRChatSession[]>(
      `${this.CHAT_ENDPOINTS.SESSIONS}?limit=${limit}&agentType=HR`
    )
    return response
  }

  /**
   * Get a specific chat session with messages
   */
  static async getChatSession(sessionId: string): Promise<HRChatSession> {
    const response = await api.get<HRChatSession>(
      this.CHAT_ENDPOINTS.SESSION_BY_ID(sessionId)
    )
    return response
  }

  /**
   * Send a chat message
   */
  static async sendChatMessage(params: {
    content: string
    sessionId?: string
    agentType: 'HR'
    confidenceThreshold?: number
    translateToSpanish?: boolean
    language?: 'en' | 'es' | 'both'
    sessionTitle?: string
  }): Promise<HRMessage> {
    const response = await api.post<HRMessage>(
      this.CHAT_ENDPOINTS.MESSAGES,
      params
    )
    return response
  }

  /**
   * Submit feedback for a message
   */
  static async submitFeedback(
    messageId: string,
    feedback: { rating: number; comment?: string }
  ): Promise<void> {
    await api.post(
      this.CHAT_ENDPOINTS.MESSAGE_FEEDBACK(messageId),
      feedback
    )
  }

  /**
   * Update a chat session (rename, change active status)
   */
  static async updateChatSession(
    sessionId: string,
    updates: { title?: string; isActive?: boolean }
  ): Promise<HRChatSession> {
    const response = await api.patch<HRChatSession>(
      this.CHAT_ENDPOINTS.SESSION_BY_ID(sessionId),
      updates
    )
    return response
  }

  /**
   * Delete (archive) a chat session
   */
  static async deleteChatSession(sessionId: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(
      this.CHAT_ENDPOINTS.SESSION_BY_ID(sessionId)
    )
    return response
  }
} 